<template>
  <v-dialog v-model="showDialog" width="600" persistent>
    <v-card>
      <v-form
        ref="form"
        :disabled="updateContactLoading"
        validate-on="invalid-input lazy"
        @submit.prevent="updateContact"
      >
        <v-card-title>
          {{ isEditing ? t("account.settings.editContact") : t("account.settings.addContact") }}
        </v-card-title>
        <v-card-text>
          <v-text-field
            v-model.trim="editEmail"
            :rules="[required, validEmail, uniqueEmail]"
            variant="outlined"
            type="email"
          >
            <template #label>
              {{ t("common.email") }}
              <span class="text-error">*</span>
            </template>
          </v-text-field>
          <v-text-field v-model.trim="editPhone" :rules="[required]" variant="outlined">
            <template #label>
              {{ t("common.phone") }}
              <span class="text-error">*</span>
            </template>
          </v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn :disabled="updateContactLoading" @click="closeDialog()">{{
            t("common.cancel")
          }}</v-btn>
          <v-btn
            type="submit"
            :loading="updateContactLoading"
            :disabled="updateContactLoading"
            color="primary"
            variant="flat"
            >{{ isEditing ? t("common.save") : t("common.add") }}</v-btn
          >
        </v-card-actions>
      </v-form>
    </v-card>
  </v-dialog>
</template>

<script lang="ts" setup>
import { useI18n } from "@/composables/useI18n";
import { useRules } from "@/composables/useRules";
import type { Contact } from "@aircap/common/account";
import { computed, ref, watch } from "vue";
import type { VForm } from "vuetify/components";

type ContactWithId = Contact & { id: string };

const { t } = useI18n();
const { required, validEmail } = useRules();
const form = ref<VForm>();

const props = defineProps<{
  modelValue: boolean;
  contact: ContactWithId | null;
  contacts: ContactWithId[];
  loading?: boolean;
}>();

const emit = defineEmits<{
  "update:modelValue": [value: boolean];
  "update:contact": [contact: ContactWithId, email: string, phone: string];
  "add:contact": [email: string, phone: string];
}>();

const showDialog = computed({
  get: () => props.modelValue,
  set: (value) => emit("update:modelValue", value),
});

const editPhone = ref("");
const editEmail = ref("");
const updateContactLoading = computed(() => props.loading || false);

const isEditing = computed(() => !!props.contact);

const uniqueEmail = computed(() => {
  return (value: string) => {
    if (!value) return true;

    const existingContact = props.contacts.find(
      (contact) =>
        contact.email.toLowerCase() === value.toLowerCase() && contact.id !== props.contact?.id,
    );

    return !existingContact || t("account.settings.contactAlreadyExists");
  };
});

watch(
  () => props.modelValue,
  (modelValue) => {
    if (modelValue) {
      editPhone.value = props.contact?.phone || "";
      editEmail.value = props.contact?.email || "";
      form.value?.resetValidation();
    }
  },
);

watch(
  () => props.contact,
  (contact) => {
    if (contact) {
      editPhone.value = contact.phone || "";
      editEmail.value = contact.email || "";
    }
  },
  { immediate: true },
);

const closeDialog = () => {
  showDialog.value = false;
};

const updateContact = async () => {
  if (!form.value) return;
  const { valid } = await form.value.validate();
  if (!valid) return;

  if (isEditing.value && props.contact) {
    emit("update:contact", props.contact, editEmail.value.trim(), editPhone.value.trim());
  } else {
    emit("add:contact", editEmail.value.trim(), editPhone.value.trim());
  }
};
</script>
